import { RefreshCw, LogOut, Inbox } from "lucide-react";
import { useAdminData } from "@/components/admin/useAdminData";
import { useEnquiryFilters } from "@/components/admin/useEnquiryFilters";
import { isOverdue } from "@/components/admin/adminUtils";
import { AdminLogin, AdminLoading } from "@/components/admin/AdminLogin";
import { StatsBar, FilterBar } from "@/components/admin/FilterBar";
import { AttributionPanel } from "@/components/admin/AttributionPanel";
import { CommentsPanel } from "@/components/admin/CommentsPanel";
import { EnquiryCard } from "@/components/admin/EnquiryCard";

const emptyMessage = (hasFilters, tab) => {
  if (hasFilters) return "No enquiries match these filters.";
  if (tab === "tests") return "No test enquiries.";
  return "No enquiries yet.";
};

function AdminHeader({ loading, onRefresh, onLogout }) {
  return (
    <div className="bg-[#0A2540] text-white">
      <div className="container mx-auto px-6 md:px-12 py-8 flex items-center justify-between">
        <div>
          <span className="text-sm text-[#38BDF8] tracking-[0.2em] uppercase font-bold">Happy Tiddlers</span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold mt-1">Website Enquiries</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onRefresh} className="grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 transition-colors" title="Refresh" data-testid="admin-refresh">
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="admin-logout">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </div>
    </div>
  );
}

function EnquiryList({ f, data }) {
  if (f.activeList.length === 0) {
    return (
      <div className="bg-white rounded-[2rem] p-16 border border-slate-100 text-center">
        <Inbox size={40} className="mx-auto text-slate-300" />
        <p className="mt-4 text-[#64748B]">{emptyMessage(f.hasFilters, f.tab)}</p>
        {f.hasFilters && <button onClick={f.clearFilters} className="mt-3 text-sm font-semibold text-[#FF7A1A] hover:underline">Clear filters</button>}
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {f.activeList.map((e) => (
        <EnquiryCard
          key={e.id}
          e={e}
          onToggleReplied={data.toggleReplied}
          onSaveNote={data.saveNote}
          busy={data.busyId === e.id}
          classFilter={f.classFilter}
          onClassClick={f.toggleClass}
        />
      ))}
    </div>
  );
}

export default function Admin() {
  const data = useAdminData();
  const f = useEnquiryFilters(data.enquiries);

  if (data.authed === null) return <AdminLoading />;
  if (!data.authed) return <AdminLogin onLogin={data.login} loading={data.loading} error={data.error} />;

  const awaitingCount = f.realEnquiries.filter((e) => !e.replied).length;
  const overdueCount = f.realEnquiries.filter(isOverdue).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body" data-testid="admin-dashboard">
      <AdminHeader loading={data.loading} onRefresh={data.refresh} onLogout={data.logout} />
      <div className="container mx-auto px-6 md:px-12 py-10">
        <StatsBar
          total={f.realEnquiries.length}
          awaitingCount={awaitingCount}
          overdueCount={overdueCount}
          sending={data.sending}
          onReminder={data.sendReminderNow}
          onDigest={data.sendDigestNow}
        />
        <AttributionPanel
          enquiries={f.realEnquiries}
          sourceFilter={f.sourceFilter}
          onToggleSource={f.toggleSource}
          onClear={() => f.setSourceFilter(null)}
          filteredList={f.activeList}
        />
        <CommentsPanel comments={data.comments} onApprove={data.onApprove} onDelete={data.onDelete} />
        <FilterBar f={f} />
        <EnquiryList f={f} data={data} />
      </div>
    </div>
  );
}
